import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, getWeekStart, DAY_LABELS, DAYS } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { ChevronLeft, ChevronRight, Plus, Trash2, CakeSlice, Sparkles, Image as ImageIcon, BookOpen, X, Check, Pencil } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";

const DEFAULT_BAKING_CATEGORIES = ["Bakst", "Dessert", "Kake", "Kjeks", "Brød", "Annet"];

export default function BakingSection({ servings }) {
  const { user, activeBoat, loadActiveBoat } = useAuth();
  const isAdmin = user?.role === "admin";
  const [weekStart, setWeekStart] = useState(getWeekStart());
  const [bakingPlan, setBakingPlan] = useState(null);
  const [bakingRecipes, setBakingRecipes] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [newItem, setNewItem] = useState({ custom_name: "", day: "monday", recipe_id: null, recipe_title: "", category: "Bakst" });
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [genImgLoading, setGenImgLoading] = useState({}); // { [itemId]: true }
  const [filterCategory, setFilterCategory] = useState("all");
  const [editCategoriesMode, setEditCategoriesMode] = useState(false);
  const [newCategoryName, setNewCategoryName] = useState("");

  // Boat-scoped editable categories. Falls back to default when the boat has none yet.
  const bakingCategories = (activeBoat?.baking_categories && activeBoat.baking_categories.length > 0)
    ? activeBoat.baking_categories
    : DEFAULT_BAKING_CATEGORIES;

  const loadData = useCallback(async () => {
    try {
      const [planRes, recipesRes] = await Promise.all([
        api.getBakingPlan(weekStart),
        api.getRecipes(true),
      ]);
      setBakingPlan(planRes.data);
      setBakingRecipes(recipesRes.data);
    } catch {
      toast.error("Kunne ikke laste bakeplan");
    }
  }, [weekStart]);

  useEffect(() => { loadData(); }, [loadData]);

  const shiftWeek = (dir) => {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + dir * 7);
    setWeekStart(d.toISOString().split("T")[0]);
  };

  const formatWeekRange = () => {
    const start = new Date(weekStart);
    const end = new Date(weekStart);
    end.setDate(end.getDate() + 6);
    const fmt = (d) => `${d.getDate()}.${d.getMonth() + 1}`;
    return `${fmt(start)} - ${fmt(end)}`;
  };

  const generateImage = useCallback(async (item) => {
    if (!item?.id) return;
    if (genImgLoading[item.id]) return;
    setGenImgLoading((p) => ({ ...p, [item.id]: true }));
    try {
      const res = await api.generateBakingImage(weekStart, item.id);
      setBakingPlan((bp) => bp && ({
        ...bp,
        items: bp.items.map((it) => it.id === item.id ? { ...it, image_url: res.data.image_url } : it),
      }));
    } catch (err) {
      // Soft-fail — keep fallback image and notify only when manually triggered.
      if (item._manual) toast.error(err.response?.data?.detail || "Kunne ikke generere bilde");
    } finally {
      setGenImgLoading((p) => ({ ...p, [item.id]: false }));
    }
  }, [weekStart, genImgLoading]);

  const addBakingItem = async () => {
    if (!bakingPlan) return;
    if (!newItem.custom_name && !newItem.recipe_id) {
      toast.error("Velg en oppskrift eller skriv inn et navn");
      return;
    }
    const item = {
      id: crypto.randomUUID(),
      recipe_id: newItem.recipe_id,
      recipe_title: newItem.recipe_title,
      custom_name: newItem.custom_name,
      day: newItem.day,
      notes: "",
      category: newItem.category || "Bakst",
    };
    const updated = { ...bakingPlan, items: [...bakingPlan.items, item] };
    setBakingPlan(updated);
    setDialogOpen(false);
    setNewItem({ custom_name: "", day: "monday", recipe_id: null, recipe_title: "", category: bakingCategories[0] || "Bakst" });
    try {
      await api.updateBakingPlan(weekStart, updated);
      toast.success("Lagt til i bakeplanen");
      // Auto-generate AI image silently for custom items (recipe-linked items already use recipe.image_url)
      if (!item.recipe_id) {
        toast.info("AI lager et bilde — vises om litt", { duration: 2500 });
        generateImage(item);
      }
    } catch { toast.error("Feil ved lagring"); }
  };

  const removeBakingItem = async (itemId) => {
    if (!bakingPlan) return;
    const updated = { ...bakingPlan, items: bakingPlan.items.filter((i) => i.id !== itemId) };
    setBakingPlan(updated);
    try {
      await api.updateBakingPlan(weekStart, updated);
    } catch { toast.error("Feil ved sletting"); }
  };

  const persistCategories = useCallback(async (next) => {
    if (!activeBoat?.id) return;
    try {
      await api.updateBoatBakingCategories(activeBoat.id, next);
      if (loadActiveBoat) loadActiveBoat();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunne ikke lagre kategorier");
    }
  }, [activeBoat?.id, loadActiveBoat]);

  const addCategory = async () => {
    const name = newCategoryName.trim();
    if (!name) return;
    if (bakingCategories.some((c) => c.toLowerCase() === name.toLowerCase())) {
      toast.error(`'${name}' finnes allerede`);
      return;
    }
    const next = [...bakingCategories, name];
    setNewCategoryName("");
    await persistCategories(next);
    toast.success(`'${name}' lagt til`);
  };

  const removeCategory = async (cat) => {
    if (bakingCategories.length <= 1) {
      toast.error("Du må ha minst én kategori");
      return;
    }
    const next = bakingCategories.filter((c) => c !== cat);
    if (filterCategory === cat) setFilterCategory("all");
    await persistCategories(next);
    toast.success(`'${cat}' fjernet`);
  };

  const handleGenerateImageClick = (item) => {
    // Wrapper to flag the call as user-triggered so we surface errors as toasts.
    generateImage({ ...item, _manual: true });
  };

  const generateBakingRecipe = async () => {
    if (!aiPrompt.trim()) return;
    setAiLoading(true);
    try {
      const res = await api.generateRecipe({
        prompt: aiPrompt,
        meal_type: "baking",
        servings,
        is_baking: true,
      });
      setBakingRecipes((prev) => [...prev, res.data]);
      toast.success(`Oppskrift "${res.data.title}" generert!`);
      // Suggest new bakst-kategori if AI used one not in the boat's list
      const cat = (res.data?.category || "").trim();
      if (cat && !bakingCategories.some((c) => c.toLowerCase() === cat.toLowerCase()) && activeBoat?.id) {
        toast.info(`AI brukte kategorien '${cat}'`, {
          description: "Vil du legge den til som en fast bakst-kategori?",
          duration: 8000,
          action: {
            label: "Ja, legg til",
            onClick: async () => {
              try {
                await api.updateBoatBakingCategories(activeBoat.id, [...bakingCategories, cat]);
                if (loadActiveBoat) loadActiveBoat();
                toast.success(`'${cat}' lagt til`);
              } catch (err) {
                toast.error(err.response?.data?.detail || "Kunne ikke lagre kategori");
              }
            },
          },
        });
      }
      setAiDialogOpen(false);
      setAiPrompt("");
    } catch {
      toast.error("Kunne ikke generere oppskrift");
    } finally {
      setAiLoading(false);
    }
  };

  // Fallback images — multiple variety so different baking items don't all show the same picture
  const images = [
    "https://images.unsplash.com/photo-1509440159596-0249088772ff?auto=format&fit=crop&q=80&w=600",  // cinnamon rolls
    "https://images.unsplash.com/photo-1486427944544-d2c246c4df14?auto=format&fit=crop&q=80&w=600",  // bread
    "https://images.unsplash.com/photo-1558961363-fa8fdf82db35?auto=format&fit=crop&q=80&w=600",    // pancakes
    "https://images.unsplash.com/photo-1555507036-ab1f4038024a?auto=format&fit=crop&q=80&w=600",    // muffin
    "https://images.unsplash.com/photo-1565958011703-44f9829ba187?auto=format&fit=crop&q=80&w=600",  // donuts
    "https://images.unsplash.com/photo-1568571780765-9276ac8b75a2?auto=format&fit=crop&q=80&w=600",  // cookies
    "https://images.unsplash.com/photo-1551024601-bec78aea704b?auto=format&fit=crop&q=80&w=600",    // macarons
    "https://images.unsplash.com/photo-1606312619070-d48b4c652a52?auto=format&fit=crop&q=80&w=600",  // croissant
    "https://images.unsplash.com/photo-1571115177098-24ec42ed204d?auto=format&fit=crop&q=80&w=600",  // chocolate cake
    "https://images.unsplash.com/photo-1578985545062-69928b1d9587?auto=format&fit=crop&q=80&w=600",  // cake slice
    "https://images.unsplash.com/photo-1464195244916-405fa0a82545?auto=format&fit=crop&q=80&w=600",  // berry tart
    "https://images.unsplash.com/photo-1517433367423-c7e5b0f35086?auto=format&fit=crop&q=80&w=600",  // waffle
  ];

  // Deterministic image picker — same baking item always gets the same image
  const pickImage = (item, idx) => {
    const seed = item.id || item.custom_name || item.recipe_title || String(idx);
    let hash = 0;
    for (let i = 0; i < seed.length; i++) {
      hash = ((hash << 5) - hash) + seed.charCodeAt(i);
      hash |= 0;
    }
    return images[Math.abs(hash) % images.length];
  };

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid="baking-section-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight text-stone-900" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            Baking
          </h1>
          <p className="text-sm text-stone-500 mt-1">Planlegg ukens bakst</p>
        </div>
        <div className="flex items-center gap-2 sm:gap-3">
          <button
            onClick={() => setAiDialogOpen(true)}
            className="inline-flex items-center justify-center rounded-md bg-orange-700 p-2 sm:px-4 sm:py-2.5 text-sm font-medium text-white hover:bg-orange-800 transition-colors gap-1.5"
            data-testid="ai-baking-btn"
            title="AI-generert oppskrift"
          >
            <Sparkles className="h-4 w-4" />
            <span className="hidden sm:inline">AI Oppskrift</span>
          </button>
          <div className="flex items-center gap-2 bg-white border border-stone-200 rounded-xl px-3 py-2">
            <button onClick={() => shiftWeek(-1)} className="p-1 hover:bg-stone-100 rounded-md transition-colors" data-testid="baking-prev-week">
              <ChevronLeft className="h-4 w-4 text-stone-500" />
            </button>
            <span className="text-sm font-medium text-stone-700 min-w-[120px] text-center" data-testid="baking-week-range">
              {formatWeekRange()}
            </span>
            <button onClick={() => shiftWeek(1)} className="p-1 hover:bg-stone-100 rounded-md transition-colors" data-testid="baking-next-week">
              <ChevronRight className="h-4 w-4 text-stone-500" />
            </button>
          </div>
        </div>
      </div>

      {/* Baking hero */}
      <Link to="/recipes?category=baking" className="relative rounded-2xl overflow-hidden mb-8 h-48 block group" data-testid="baking-hero">
        <img
          src="https://images.unsplash.com/photo-1509440159596-0249088772ff?auto=format&fit=crop&q=80&w=1200"
          alt="Baking"
          className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-stone-900/60 to-transparent flex items-center pl-8">
          <div>
            <h2 className="text-3xl font-bold text-white" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              Ukens Bakst
            </h2>
            <p className="text-stone-200 text-sm mt-1">{bakingPlan?.items?.length || 0} planlagte bakstvarer · trykk for å se oppskrifter</p>
          </div>
        </div>
      </Link>

      {/* Category filter chips (editable) */}
      <div className="flex items-start justify-between gap-2 mb-4 flex-wrap" data-testid="baking-category-chips-wrapper">
        <div className="flex gap-2 flex-wrap flex-1 min-w-0" data-testid="baking-category-chips">
          <button
            onClick={() => setFilterCategory("all")}
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all border ${filterCategory === "all" ? "bg-navy-800 text-white border-navy-800" : "bg-white text-stone-600 border-stone-200 hover:bg-stone-50"}`}
            data-testid="baking-chip-all"
          >
            Alle <span className="opacity-70">({bakingPlan?.items?.length || 0})</span>
          </button>
          {bakingCategories.map((cat) => {
            const count = (bakingPlan?.items || []).filter((i) => (i.category || "Bakst") === cat).length;
            const active = filterCategory === cat;
            return (
              <span key={cat} className="inline-flex items-stretch">
                <button
                  onClick={() => setFilterCategory(cat)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-l-full text-xs font-medium transition-all border ${active ? "bg-navy-800 text-white border-navy-800" : "bg-white text-stone-600 border-stone-200 hover:bg-stone-50"} ${editCategoriesMode ? "" : "rounded-r-full"}`}
                  data-testid={`baking-chip-${cat}`}
                >
                  {cat} <span className="opacity-70">({count})</span>
                </button>
                {editCategoriesMode && (
                  <button
                    onClick={() => removeCategory(cat)}
                    className="inline-flex items-center justify-center px-2 rounded-r-full border border-l-0 border-red-200 bg-red-50 text-red-600 hover:bg-red-100 transition-colors"
                    title={`Fjern '${cat}'`}
                    data-testid={`baking-chip-remove-${cat}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </span>
            );
          })}
          {editCategoriesMode && (
            <form
              onSubmit={(e) => { e.preventDefault(); addCategory(); }}
              className="inline-flex items-center gap-1"
              data-testid="baking-add-category-form"
            >
              <input
                autoFocus
                value={newCategoryName}
                onChange={(e) => setNewCategoryName(e.target.value)}
                placeholder="Ny kategori"
                className="rounded-full border border-stone-200 px-3 py-1 text-xs outline-none focus:border-navy-800 w-32"
                data-testid="baking-new-category-input"
              />
              <button
                type="submit"
                className="inline-flex items-center justify-center rounded-full bg-emerald-600 text-white px-2 py-1 text-xs hover:bg-emerald-700"
                data-testid="baking-add-category-submit"
                title="Legg til"
              >
                <Check className="h-3 w-3" />
              </button>
            </form>
          )}
        </div>
        <button
          onClick={() => setEditCategoriesMode((v) => !v)}
          className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors flex-shrink-0 ${editCategoriesMode ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-stone-200 bg-white text-stone-500 hover:bg-stone-50"}`}
          data-testid="baking-edit-categories-toggle"
          title="Rediger kategorier"
        >
          <Pencil className="h-3 w-3" />
          {editCategoriesMode ? "Ferdig" : "Rediger"}
        </button>
      </div>

      {/* Items grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
        {(bakingPlan?.items || []).filter((item) => filterCategory === "all" || (item.category || "Bakst") === filterCategory).map((item, idx) => {
          const displayName = item.custom_name || item.recipe_title;
          // Try to resolve a recipe by direct id, else by fuzzy title match
          let linkedId = item.recipe_id || null;
          let linkedRecipe = item.recipe_id ? bakingRecipes.find((r) => r.id === item.recipe_id) : null;
          if (!linkedId && (item.custom_name || item.recipe_title)) {
            const needle = (item.custom_name || item.recipe_title || "").trim().toLowerCase();
            const match = bakingRecipes.find((r) => r.title && r.title.trim().toLowerCase() === needle)
              || bakingRecipes.find((r) => r.title && r.title.toLowerCase().includes(needle))
              || bakingRecipes.find((r) => r.title && needle.includes(r.title.toLowerCase()));
            if (match) { linkedId = match.id; linkedRecipe = match; }
          }
          // Prefer item.image_url (manually generated), then linked recipe's image, then unique deterministic fallback per item (hashed)
          const imgSrc = item.image_url || linkedRecipe?.image_url || pickImage(item, idx);
          const hasLink = !!linkedId;
          return (
            <div
              key={item.id}
              className="baking-card bg-white border border-stone-100 rounded-xl overflow-hidden animate-fade-in"
              style={{ animationDelay: `${idx * 0.05}s` }}
              data-testid={`baking-item-${item.id}`}
            >
              {hasLink ? (
                <Link to={`/recipes/${linkedId}`} className="block h-36 overflow-hidden relative group" data-testid={`baking-img-link-${item.id}`} title="Åpne oppskrift">
                  <img src={imgSrc} alt={displayName} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all">
                    <BookOpen className="h-6 w-6 text-white" />
                  </div>
                </Link>
              ) : (
                <div className="h-36 overflow-hidden relative">
                  <img src={imgSrc} alt={displayName} className="w-full h-full object-cover" />
                </div>
              )}
              <div className="p-4">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    {hasLink ? (
                      <Link to={`/recipes/${linkedId}`} className="block hover:text-amber-700 transition-colors" data-testid={`baking-title-link-${item.id}`}>
                        <h3 className="text-base font-semibold text-stone-800 hover:text-amber-700 truncate">{displayName}</h3>
                      </Link>
                    ) : (
                      <h3 className="text-base font-semibold text-stone-800 truncate">{displayName}</h3>
                    )}
                    <p className="text-xs text-stone-400 mt-1">
                      {DAY_LABELS[DAYS.indexOf(item.day)]} · <span className="text-stone-500">{item.category || "Bakst"}</span>
                    </p>
                  </div>
                  <button
                    onClick={() => removeBakingItem(item.id)}
                    className="p-1.5 hover:bg-red-50 rounded-md text-stone-400 hover:text-red-500 transition-colors flex-shrink-0"
                    data-testid={`remove-baking-${item.id}`}
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                {!item.image_url && (
                  <button
                    onClick={() => handleGenerateImageClick(item)}
                    disabled={genImgLoading[item.id]}
                    className="mt-3 w-full inline-flex items-center justify-center gap-1.5 rounded-md bg-orange-50 border border-orange-200 text-orange-700 px-2 py-1.5 text-xs font-semibold hover:bg-orange-100 transition-colors disabled:opacity-60"
                    data-testid={`gen-baking-img-${item.id}`}
                    title="Generer AI-bilde (gratis)"
                  >
                    {genImgLoading[item.id] ? (
                      <>
                        <div className="w-3 h-3 border-2 border-orange-700 border-t-transparent rounded-full animate-spin" />
                        Genererer...
                      </>
                    ) : (
                      <>
                        <Sparkles className="h-3 w-3" /> Generer bilde
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          );
        })}

        {/* Add new card */}
        <button
          onClick={() => setDialogOpen(true)}
          className="min-h-[220px] bg-white border-2 border-dashed border-stone-200 rounded-xl flex flex-col items-center justify-center gap-3 text-stone-400 hover:text-navy-800 hover:border-seawater-500 hover:bg-sailcloth-100/30 transition-all"
          data-testid="add-baking-btn"
        >
          <Plus className="h-8 w-8" />
          <span className="text-sm font-medium">Legg til bakst</span>
        </button>
      </div>

      {/* Add baking dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md" data-testid="add-baking-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "'Cormorant Garamond', serif" }}>Legg til bakst</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-stone-700 block mb-1">Dag</label>
              <Select value={newItem.day} onValueChange={(v) => setNewItem({ ...newItem, day: v })}>
                <SelectTrigger className="border-stone-200" data-testid="baking-day-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DAYS.map((d, i) => (
                    <SelectItem key={d} value={d}>{DAY_LABELS[i]}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-stone-700 block mb-1">Kategori</label>
              <Select value={newItem.category} onValueChange={(v) => setNewItem({ ...newItem, category: v })}>
                <SelectTrigger className="border-stone-200" data-testid="baking-category-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {bakingCategories.map((c) => (<SelectItem key={c} value={c}>{c}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-sm font-medium text-stone-700 block mb-1">Egendefinert navn</label>
              <input
                value={newItem.custom_name}
                onChange={(e) => setNewItem({ ...newItem, custom_name: e.target.value, recipe_id: null, recipe_title: "" })}
                placeholder="F.eks. Kanelboller, Sjokoladekake..."
                className="w-full rounded-md border border-stone-200 bg-white px-3 py-2 text-sm focus:border-navy-800 focus:ring-1 focus:ring-navy-800 outline-none transition-colors"
                data-testid="baking-custom-name-input"
              />
            </div>
            {bakingRecipes.length > 0 && (
              <div>
                <label className="text-sm font-medium text-stone-700 block mb-1">Eller velg oppskrift</label>
                <div className="max-h-40 overflow-y-auto space-y-1 border border-stone-100 rounded-lg p-2">
                  {bakingRecipes.map((r) => (
                    <button
                      key={r.id}
                      onClick={() => setNewItem({ ...newItem, recipe_id: r.id, recipe_title: r.title, custom_name: "" })}
                      className={`w-full text-left p-2 rounded-md text-sm transition-colors ${
                        newItem.recipe_id === r.id ? "bg-sailcloth-100 text-navy-800 font-medium" : "hover:bg-stone-50 text-stone-700"
                      }`}
                      data-testid={`baking-recipe-option-${r.id}`}
                    >
                      {r.title}
                    </button>
                  ))}
                </div>
              </div>
            )}
            <button
              onClick={addBakingItem}
              className="w-full inline-flex items-center justify-center rounded-md bg-navy-800 px-4 py-2.5 text-sm font-medium text-white hover:bg-navy-900 transition-colors"
              data-testid="confirm-add-baking-btn"
            >
              Legg til
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* AI Generate dialog */}
      <Dialog open={aiDialogOpen} onOpenChange={setAiDialogOpen}>
        <DialogContent className="max-w-md" data-testid="ai-baking-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
              <Sparkles className="h-5 w-5 text-orange-700" />
              AI Bakeoppskrift
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-stone-700 block mb-1">Hva vil du bake?</label>
              <textarea
                value={aiPrompt}
                onChange={(e) => setAiPrompt(e.target.value)}
                placeholder="F.eks. Saftige kanelboller med kardemomme..."
                className="w-full rounded-md border border-stone-200 bg-white px-3 py-2 text-sm focus:border-navy-800 focus:ring-1 focus:ring-navy-800 outline-none transition-colors min-h-[80px] resize-none"
                data-testid="ai-baking-prompt"
              />
            </div>
            {aiLoading ? (
              <div className="space-y-3">
                <Skeleton className="h-4 w-3/4" />
                <Skeleton className="h-4 w-1/2" />
                <Skeleton className="h-4 w-2/3" />
                <p className="text-xs text-stone-400 text-center">Genererer oppskrift med AI...</p>
              </div>
            ) : (
              <button
                onClick={generateBakingRecipe}
                className="w-full inline-flex items-center justify-center rounded-md bg-orange-700 px-4 py-2.5 text-sm font-medium text-white hover:bg-orange-800 transition-colors gap-2"
                data-testid="generate-baking-recipe-btn"
              >
                <CakeSlice className="h-4 w-4" />
                Generer oppskrift
              </button>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
